(() => {
  if (window.ZalvaroProductViewer) return;

  class ZalvaroProductViewer {
    constructor(gallery) {
      this.gallery = gallery;
      this.dialog = document.getElementById(gallery.dataset.viewerId);
      this.triggers = Array.from(gallery.querySelectorAll('[data-zalvaro-viewer-trigger]'));
      this.cells = Array.from(gallery.querySelectorAll('.zalvaro-cell'));
      if (!this.dialog || !this.triggers.length) return;

      this.image = this.dialog.querySelector('[data-viewer-image]');
      this.canvas = this.dialog.querySelector('[data-viewer-canvas]');
      this.currentLabel = this.dialog.querySelector('[data-viewer-current]');
      this.zoomLabel = this.dialog.querySelector('[data-viewer-zoom-value]');
      this.fullscreenButton = this.dialog.querySelector('[data-viewer-fullscreen]');
      this.closeButton = this.dialog.querySelector('[data-viewer-close]');
      this.status = this.dialog.querySelector('[data-viewer-status]');
      this.statusTemplate = this.dialog.dataset.statusTemplate || 'Image [current] of [total]';
      this.enterFullscreenLabel = this.dialog.dataset.enterFullscreen || 'Enter full screen';
      this.exitFullscreenLabel = this.dialog.dataset.exitFullscreen || 'Exit full screen';
      this.thumbs = Array.from(this.dialog.querySelectorAll('[data-viewer-thumb]'));
      this.mobileCounter = gallery.querySelector('.zalvaro-mobile-counter__current');
      this.rail = gallery.querySelector('.zalvaro-grid');

      this.index = 0;
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.pointers = new Map();
      this.lastTapAt = 0;
      this.returnFocus = null;
      this.railFrame = null;
      this.pointerMoved = false;
      this.gestureHadMultiplePointers = false;
      this.imageRequest = 0;
      this.fitWidth = 0;
      this.fitHeight = 0;
      this.panViewportWidth = 0;
      this.panViewportHeight = 0;
      this.viewportFrame = null;
      this.fitFrame = null;
      this.fitTimer = null;
      this.hoverFrame = null;
      this.renderFrame = null;
      this.wheelTimer = null;
      this.lastRenderedZoom = null;
      this.lastRenderedZoomed = null;

      this.bindEvents();
      this.updateMobileCounter();
      gallery.dataset.viewerReady = 'true';
    }

    bindEvents() {
      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.open(index, trigger));
      });

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.showImage(index));
      });

      this.closeButton.addEventListener('pointerdown', (event) => event.stopPropagation());
      this.closeButton.addEventListener('click', (event) => this.close(event));
      this.dialog.querySelector('[data-viewer-previous]')?.addEventListener('click', () => this.previous());
      this.dialog.querySelector('[data-viewer-next]')?.addEventListener('click', () => this.next());
      this.dialog.querySelector('[data-viewer-zoom-in]').addEventListener('click', () => this.setZoom(this.scale + 0.5));
      this.dialog.querySelector('[data-viewer-zoom-out]').addEventListener('click', () => this.setZoom(this.scale - 0.5));
      this.fullscreenButton.addEventListener('pointerdown', (event) => event.stopPropagation());
      this.fullscreenButton.addEventListener('click', (event) => this.toggleFullscreen(event));

      this.dialog.addEventListener('close', () => this.onClose());
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('fullscreenchange', () => {
        this.syncFullscreenState();
        this.onViewportChange();
      });
      this.canvas.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      this.canvas.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.canvas.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.canvas.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.canvas.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.canvas.addEventListener('pointerleave', () => delete this.dialog.dataset.hoverPanning);

      this.rail.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.railFrame);
          this.railFrame = requestAnimationFrame(() => this.updateMobileCounter());
        },
        { passive: true }
      );
      window.addEventListener('resize', () => this.onViewportChange(), { passive: true });
      window.addEventListener('orientationchange', () => this.onViewportChange(), { passive: true });
      window.visualViewport?.addEventListener('resize', () => this.onViewportChange(), { passive: true });
    }

    get count() {
      return this.triggers.length;
    }

    open(index, trigger) {
      this.returnFocus = trigger;
      this.showImage(index, false);
      document.body.classList.add('z-product-viewer-open');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.scheduleFit(true);
      requestAnimationFrame(() => this.closeButton.focus({ preventScroll: true }));
    }

    close(event) {
      event?.preventDefault();
      event?.stopPropagation();

      try {
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.onClose();
        }
      } catch (_error) {
        this.dialog.removeAttribute('open');
        this.onClose();
      }
    }

    onClose() {
      document.body.classList.remove('z-product-viewer-open');
      if (document.fullscreenElement) {
        Promise.resolve(document.exitFullscreen?.()).catch(() => {});
      }
      delete this.dialog.dataset.pseudoFullscreen;
      delete this.dialog.dataset.wheelZooming;
      window.clearTimeout(this.wheelTimer);
      this.syncFullscreenState();
      this.resetZoom();
      this.returnFocus?.focus({ preventScroll: true });
    }

    showImage(index, announce = true) {
      this.index = (index + this.count) % this.count;
      const trigger = this.triggers[this.index];
      const requestId = ++this.imageRequest;
      const nextSource = trigger.dataset.fullSrc;
      const fallbackSource = trigger.querySelector('img')?.currentSrc || trigger.querySelector('img')?.src;

      this.resetZoom();
      this.dialog.dataset.loading = 'true';
      const loader = new Image();
      loader.decoding = 'async';

      const applyImage = (source) => {
        if (requestId !== this.imageRequest) return;
        this.image.alt = trigger.dataset.imageAlt || '';
        this.image.width = Number(trigger.dataset.imageWidth) || 1800;
        this.image.height = Number(trigger.dataset.imageHeight) || 2400;
        this.image.src = source;
        delete this.dialog.dataset.loading;
        this.scheduleFit(true);
        this.image.decode?.().then(() => this.scheduleFit(this.scale === 1)).catch(() => {});
      };

      loader.addEventListener('load', () => applyImage(nextSource), { once: true });
      loader.addEventListener('error', () => applyImage(fallbackSource || nextSource), { once: true });
      loader.src = nextSource;
      if (loader.complete && loader.naturalWidth) applyImage(nextSource);

      this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
      this.thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute('aria-current', thumbIndex === this.index ? 'true' : 'false');
      });
      if (this.dialog.open && window.innerWidth >= 750) {
        this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      if (announce) {
        this.status.textContent = this.statusTemplate
          .replace('[current]', String(this.index + 1))
          .replace('[total]', String(this.count));
      }
      this.preloadAdjacent();
    }

    async toggleFullscreen(event) {
      event?.preventDefault();
      event?.stopPropagation();
      const active = Boolean(document.fullscreenElement) || this.dialog.dataset.pseudoFullscreen === 'true';

      if (!active) this.dialog.dataset.pseudoFullscreen = 'true';
      else delete this.dialog.dataset.pseudoFullscreen;
      this.syncFullscreenState();

      try {
        if (active && document.fullscreenElement) {
          await document.exitFullscreen();
        } else if (!active && this.dialog.requestFullscreen) {
          await this.dialog.requestFullscreen({ navigationUI: 'hide' });
        } else if (!active && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
        }
      } catch (_error) {}

      if (!active && document.fullscreenElement) delete this.dialog.dataset.pseudoFullscreen;

      this.syncFullscreenState();
      requestAnimationFrame(() => {
        this.syncMobileViewport();
        this.fitImage();
        this.resetZoom();
      });
    }

    syncFullscreenState() {
      const active = Boolean(document.fullscreenElement) || this.dialog.dataset.pseudoFullscreen === 'true';
      this.fullscreenButton.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.fullscreenButton.setAttribute(
        'aria-label',
        active ? this.exitFullscreenLabel : this.enterFullscreenLabel
      );
    }

    previous() {
      this.showImage(this.index - 1);
    }

    next() {
      this.showImage(this.index + 1);
    }

    preloadAdjacent() {
      if (this.count < 2) return;
      [this.index - 1, this.index + 1].forEach((index) => {
        const adjacent = (index + this.count) % this.count;
        const preload = new Image();
        preload.src = this.triggers[adjacent].dataset.fullSrc;
      });
    }

    setZoom(value, focalPoint) {
      const nextScale = Math.min(4, Math.max(1, Math.round(value * 10) / 10));

      if (focalPoint && nextScale !== this.scale) {
        const canvasRect = this.canvas.getBoundingClientRect();
        const cursorX = focalPoint.x - (canvasRect.left + canvasRect.width / 2);
        const cursorY = focalPoint.y - (canvasRect.top + canvasRect.height / 2);
        const ratio = nextScale / this.scale;
        this.panX = cursorX - (cursorX - this.panX) * ratio;
        this.panY = cursorY - (cursorY - this.panY) * ratio;
      }

      this.scale = nextScale;
      if (this.scale === 1) {
        this.panX = 0;
        this.panY = 0;
        delete this.dialog.dataset.hoverPanning;
      }
      this.clampPan();
      this.renderTransform();
    }

    resetZoom() {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.renderTransform(true);
    }

    syncMobileViewport() {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      if (viewportHeight) this.dialog.style.setProperty('--zv-mobile-height', `${Math.round(viewportHeight)}px`);
    }

    scheduleFit(shouldReset = false) {
      cancelAnimationFrame(this.fitFrame);
      window.clearTimeout(this.fitTimer);

      this.fitFrame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.dialog.open) return;
          this.syncMobileViewport();
          this.fitImage();
          if (shouldReset) this.resetZoom();

          this.fitTimer = window.setTimeout(() => {
            if (!this.dialog.open) return;
            this.fitImage();
            if (shouldReset && this.scale === 1) this.resetZoom();
          }, 120);
        });
      });
    }

    fitImage() {
      if (!this.canvas || !this.image) return;

      const styles = getComputedStyle(this.canvas);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      const availableWidth = Math.max(1, this.canvas.clientWidth - paddingLeft - paddingRight);
      const availableHeight = Math.max(1, this.canvas.clientHeight - paddingTop - paddingBottom);
      const naturalWidth = Number(this.image.getAttribute('width')) || this.image.naturalWidth || 1;
      const naturalHeight = Number(this.image.getAttribute('height')) || this.image.naturalHeight || 1;
      const fitRatio = Math.min(availableWidth / naturalWidth, availableHeight / naturalHeight);

      this.fitWidth = Math.max(1, naturalWidth * fitRatio);
      this.fitHeight = Math.max(1, naturalHeight * fitRatio);
      this.panViewportWidth = availableWidth;
      this.panViewportHeight = availableHeight;
      this.image.style.setProperty('--fit-width', `${this.fitWidth}px`);
      this.image.style.setProperty('--fit-height', `${this.fitHeight}px`);
      this.image.style.setProperty('--fit-left', `${paddingLeft + availableWidth / 2}px`);
      this.image.style.setProperty('--fit-top', `${paddingTop + availableHeight / 2}px`);
      this.clampPan();
      this.renderTransform();
    }

    onViewportChange() {
      cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = requestAnimationFrame(() => {
        this.syncMobileViewport();
        this.scheduleFit(false);
        this.updateMobileCounter();
      });
    }

    clampPan() {
      const { maxX, maxY } = this.getPanBounds();
      this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
      this.panY = Math.min(maxY, Math.max(-maxY, this.panY));
    }

    getPanBounds() {
      return {
        maxX: Math.max(0, (this.fitWidth * this.scale - this.panViewportWidth) / 2),
        maxY: Math.max(0, (this.fitHeight * this.scale - this.panViewportHeight) / 2),
      };
    }

    renderTransform(immediate = false) {
      if (!this.image) return;

      if (immediate) {
        cancelAnimationFrame(this.renderFrame);
        this.renderFrame = null;
        this.flushTransform();
        return;
      }

      if (this.renderFrame) return;
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null;
        this.flushTransform();
      });
    }

    flushTransform() {
      const zoomed = this.scale > 1;
      const zoomPercent = Math.round(this.scale * 100);
      const panX = Math.round(this.panX * 100) / 100;
      const panY = Math.round(this.panY * 100) / 100;

      this.image.style.transform = `translate3d(calc(-50% + ${panX}px), calc(-50% + ${panY}px), 0) scale(${this.scale})`;

      if (zoomed !== this.lastRenderedZoomed) {
        this.dialog.dataset.zoomed = zoomed ? 'true' : 'false';
        this.lastRenderedZoomed = zoomed;
      }

      if (zoomPercent !== this.lastRenderedZoom) {
        this.zoomLabel.textContent = `${zoomPercent}%`;
        this.lastRenderedZoom = zoomPercent;
      }
    }

    onWheel(event) {
      event.preventDefault();
      this.dialog.dataset.wheelZooming = 'true';
      window.clearTimeout(this.wheelTimer);
      const step = event.deltaY < 0 ? 0.25 : -0.25;
      this.setZoom(this.scale + step, { x: event.clientX, y: event.clientY });
      this.wheelTimer = window.setTimeout(() => {
        delete this.dialog.dataset.wheelZooming;
      }, 100);
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      delete this.dialog.dataset.hoverPanning;
      cancelAnimationFrame(this.hoverFrame);
      this.canvas.setPointerCapture?.(event.pointerId);
      const isFirstPointer = this.pointers.size === 0;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (isFirstPointer) {
        this.pointerMoved = false;
        this.gestureHadMultiplePointers = false;
        this.gestureStart = {
          x: event.clientX,
          y: event.clientY,
          panX: this.panX,
          panY: this.panY,
          time: performance.now(),
        };
      }

      if (this.pointers.size === 2) {
        this.gestureHadMultiplePointers = true;
        this.lastTapAt = 0;
        const points = Array.from(this.pointers.values());
        this.pinchStart = {
          distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y),
          scale: this.scale,
        };
        this.dialog.dataset.pinching = 'true';
      } else if (this.scale > 1) {
        this.dialog.dataset.dragging = 'true';
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) {
        if (event.pointerType === 'mouse' && event.buttons === 0 && this.scale > 1 && window.innerWidth >= 750) {
          cancelAnimationFrame(this.hoverFrame);
          this.hoverFrame = requestAnimationFrame(() => {
            const rect = this.canvas.getBoundingClientRect();
            const normalizedX = Math.min(1, Math.max(-1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
            const normalizedY = Math.min(1, Math.max(-1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
            const { maxX, maxY } = this.getPanBounds();

            this.panX = -normalizedX * maxX;
            this.panY = -normalizedY * maxY;
            this.dialog.dataset.hoverPanning = 'true';
            this.renderTransform(true);
          });
        }
        return;
      }
      const previous = this.pointers.get(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (Math.hypot(event.clientX - this.gestureStart.x, event.clientY - this.gestureStart.y) > 5) {
        this.pointerMoved = true;
      }

      if (this.pointers.size === 2 && this.pinchStart) {
        const points = Array.from(this.pointers.values());
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        this.setZoom(this.pinchStart.scale * (distance / Math.max(this.pinchStart.distance, 1)));
        return;
      }

      if (this.scale > 1) {
        this.panX += event.clientX - previous.x;
        this.panY += event.clientY - previous.y;
        this.clampPan();
        this.renderTransform();
      }
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const deltaX = event.clientX - this.gestureStart.x;
      const deltaY = event.clientY - this.gestureStart.y;
      const gestureDuration = performance.now() - this.gestureStart.time;
      const hadMultiplePointers = this.gestureHadMultiplePointers;
      this.pointers.delete(event.pointerId);
      this.canvas.releasePointerCapture?.(event.pointerId);

      if (this.pointers.size < 2) {
        delete this.dialog.dataset.pinching;
        this.pinchStart = null;
      }
      if (this.pointers.size === 0) {
        this.renderTransform(true);
        delete this.dialog.dataset.dragging;
      }

      if (hadMultiplePointers) {
        if (this.pointers.size === 0) {
          this.pointerMoved = false;
          this.gestureHadMultiplePointers = false;
        }
        return;
      }

      const swipeThreshold = Math.max(120, this.canvas.clientWidth * 0.34);
      const isDeliberateSwipe =
        this.scale === 1 &&
        event.pointerType === 'touch' &&
        gestureDuration < 1200 &&
        Math.abs(deltaX) >= swipeThreshold &&
        Math.abs(deltaX) > Math.abs(deltaY) * 1.6;

      if (isDeliberateSwipe) {
        this.lastTapAt = 0;
        deltaX > 0 ? this.previous() : this.next();
        return;
      }

      if (this.pointerMoved || this.pointers.size > 0) return;

      if (event.pointerType === 'touch') {
        const now = Date.now();
        if (now - this.lastTapAt < 320) {
          this.setZoom(this.scale > 1 ? 1 : 2.5, { x: event.clientX, y: event.clientY });
          this.lastTapAt = 0;
        } else {
          this.lastTapAt = now;
        }
      } else {
        this.setZoom(this.scale > 1 ? 1 : 2.25, { x: event.clientX, y: event.clientY });
      }
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') this.previous();
      if (event.key === 'ArrowRight') this.next();
      if (event.key === '+' || event.key === '=') this.setZoom(this.scale + 0.5);
      if (event.key === '-') this.setZoom(this.scale - 0.5);
      if (event.key === '0') this.resetZoom();
      if (event.key.toLowerCase() === 'f') this.toggleFullscreen();
    }

    updateMobileCounter() {
      if (!this.mobileCounter || !this.rail.clientWidth) return;
      const visibleIndex = Math.min(
        this.cells.length - 1,
        Math.max(0, Math.round(this.rail.scrollLeft / this.rail.clientWidth))
      );
      this.mobileCounter.textContent = visibleIndex + 1;
    }
  }

  function initializeViewers(scope = document) {
    scope.querySelectorAll('[data-zalvaro-gallery]:not([data-viewer-ready])').forEach((gallery) => {
      new ZalvaroProductViewer(gallery);
    });
  }

  window.ZalvaroProductViewer = ZalvaroProductViewer;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeViewers());
  } else {
    initializeViewers();
  }

  document.addEventListener('shopify:section:load', (event) => initializeViewers(event.target));
})();
